export type VehicleStatus = 'active' | 'workshop' | 'inactive';
export type ReminderDueState = 'ok' | 'soon' | 'overdue' | 'done';
export type ReminderType =
  | 'oil_change'
  | 'inspection'
  | 'hu_au'
  | 'tire_change'
  | 'insurance'
  | 'lease_end'
  | 'other';
export type RepairStatus = 'open' | 'in_progress' | 'done';
export type FuelProvider = 'dkv' | 'aral' | 'shell' | 'total' | 'uta' | 'other';
export type DocumentCategory = 'contract' | 'insurance' | 'invoice' | 'repair' | 'document';

export type FuhrparkDriver = {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  initials: string;
  color_idx: number;
  position: string;
};

export type EmployeePickerItem = {
  id: string;
  first_name: string;
  last_name: string;
  color_idx: number;
  position: string;
};

export type VehicleListItem = {
  id: string;
  brand: string;
  model: string;
  plate: string;
  year: number | null;
  first_registration: string | null;
  mileage: number;
  vin: string;
  assigned_employee_id: string | null;
  status: VehicleStatus;
  has_photo: boolean;
  driver: FuhrparkDriver | null;
  has_due: boolean;
  next_reminder: {
    id: string;
    label: string;
    due_on: string | null;
    days: number | null;
    due_state: ReminderDueState;
  } | null;
  fuel: {
    provider: FuelProvider;
    last_four: string;
    monthly_limit_cents: number;
  } | null;
};

export type FuhrparkListResponse = {
  items: VehicleListItem[];
  stats: { total: number; active: number; due: number; workshop: number };
  urgent: Array<{
    vehicle_id: string;
    model: string;
    plate: string;
    driver_name: string | null;
    reminder_id: string;
    label: string;
    due_on: string | null;
    days: number | null;
    due_state: ReminderDueState;
  }>;
};

export type VehicleReminder = {
  id: string;
  vehicle_id: string;
  reminder_type: ReminderType;
  label: string;
  interval_note: string;
  interval_months: number | null;
  interval_km: number | null;
  due_soon_days: number | null;
  due_soon_km: number | null;
  last_done_on: string | null;
  last_done_mileage: number | null;
  due_on: string | null;
  due_mileage: number | null;
  completed_at: string | null;
  display_label: string;
  due_state: ReminderDueState;
  days: number | null;
};

export type VehicleServiceEvent = {
  id: string;
  vehicle_id: string;
  reminder_id: string | null;
  repair_id: string | null;
  source: 'reminder' | 'repair';
  reminder_type: ReminderType | null;
  title: string;
  performed_on: string;
  mileage: number | null;
  workshop: string;
  cost_cents: number | null;
  note: string;
  created_at: string;
  documents: Array<{
    id: string;
    display_name: string;
    filename: string;
    mime_type: string | null;
  }>;
};

export type VehicleRepair = {
  id: string;
  vehicle_id: string;
  title: string;
  workshop: string;
  repair_date: string | null;
  cost_cents: number | null;
  status: RepairStatus;
  created_at: string;
};

export type VehicleDocument = {
  id: string;
  vehicle_id: string;
  category: DocumentCategory;
  display_name: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number;
  service_event_id?: string | null;
  created_at: string;
};

export type VehicleFuelCard = {
  id: string;
  vehicle_id: string;
  provider: FuelProvider;
  last_four: string;
  monthly_limit_cents: number;
  valid_until: string | null;
  external_card_number?: string | null;
};

export type FuelTransactionSource = 'manual' | 'dkv';

export type VehicleFuelTransaction = {
  id: string;
  vehicle_id: string;
  fuel_card_id: string | null;
  occurred_on: string;
  station: string;
  liters_milli: number | null;
  amount_cents: number;
  /** `dkv` rows come from the DKV import and are read-only in the UI. */
  source?: FuelTransactionSource;
  external_ref?: string | null;
  created_at: string;
};

export type VehicleDetail = {
  vehicle: VehicleListItem & { hu_due_on: string | null };
  reminders: VehicleReminder[];
  service_events: VehicleServiceEvent[];
  repairs: VehicleRepair[];
  documents: VehicleDocument[];
  fuel: {
    card: VehicleFuelCard | null;
    used_cents: number;
    month_from: string;
    month_to: string;
    transactions: VehicleFuelTransaction[];
  } | null;
};

export const REMINDER_TYPE_OPTIONS: Array<{ value: ReminderType; label: string }> = [
  { value: 'oil_change', label: 'Ölwechsel' },
  { value: 'inspection', label: 'Inspektion / Service' },
  { value: 'hu_au', label: 'HU/AU (TÜV)' },
  { value: 'tire_change', label: 'Reifenwechsel' },
  { value: 'insurance', label: 'Versicherung' },
  { value: 'lease_end', label: 'Leasing-Ende' },
  { value: 'other', label: 'Sonstiges' },
];

export const FUEL_PROVIDER_OPTIONS: Array<{ value: FuelProvider | 'none'; label: string }> = [
  { value: 'none', label: 'Keine' },
  { value: 'dkv', label: 'DKV' },
  { value: 'aral', label: 'Aral' },
  { value: 'shell', label: 'Shell' },
  { value: 'total', label: 'Total' },
  { value: 'uta', label: 'UTA' },
  { value: 'other', label: 'Sonstiges' },
];

export const DOCUMENT_CATEGORY_OPTIONS: Array<{ value: DocumentCategory; label: string }> = [
  { value: 'contract', label: 'Vertrag' },
  { value: 'insurance', label: 'Versicherung' },
  { value: 'invoice', label: 'Rechnung' },
  { value: 'repair', label: 'Reparatur' },
  { value: 'document', label: 'Dokument' },
];

export const REPAIR_STATUS_OPTIONS: Array<{ value: RepairStatus; label: string }> = [
  { value: 'open', label: 'Offen' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'done', label: 'Abgeschlossen' },
];

export const VEHICLE_STATUS_OPTIONS: Array<{ value: VehicleStatus; label: string }> = [
  { value: 'active', label: 'Aktiv' },
  { value: 'workshop', label: 'In Werkstatt' },
  { value: 'inactive', label: 'Inaktiv' },
];
